import type { AnyID, Hole, SceneObjects, Walls } from '@types'
import { toVector3 } from '@utils/conversion/toVector3'
import { areSnapAdjacent } from '@utils/sceneObjects/snappedSides'
import { Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'

export type WallSpan = { start: number; end: number }

type BoundingBoxEntry = { type: string; boundingBox: OBB }

export function getItemCenter(
  item: SceneObjects,
  sceneBoundingBoxes?: Map<string, BoundingBoxEntry>,
): Vector3 {
  const center = sceneBoundingBoxes?.get(item.id)?.boundingBox?.center
  if (center && typeof center.clone === 'function') {
    return center.clone()
  }
  if (center && typeof center.x === 'number') {
    return new Vector3(center.x, center.y ?? 0, center.z ?? 0)
  }

  if ('matrix' in item && item.matrix) {
    const pos = new Vector3()
    item.matrix.decompose(pos, new Quaternion(), new Vector3())
    return pos
  }

  return new Vector3(
    'x' in item ? (item.x ?? 0) : 0,
    'y' in item ? (item.y ?? 0) : 0,
    'z' in item ? (item.z ?? 0) : 0,
  )
}

export function distanceXZ(a: Vector3, b: Vector3): number {
  return Math.hypot(a.x - b.x, a.z - b.z)
}

export function getItemOBB(
  item: SceneObjects,
  sceneBoundingBoxes?: Map<string, BoundingBoxEntry>,
): OBB | null {
  return sceneBoundingBoxes?.get(item.id)?.boundingBox ?? null
}

function isUsableOBB(obb: OBB | null | undefined): obb is OBB {
  const center = obb?.center
  return Boolean(
    obb &&
      center &&
      typeof center.y === 'number' &&
      Number.isFinite(center.y) &&
      typeof obb.intersectsOBB === 'function',
  )
}

export function obbOverlapAtSimilarHeight(
  a: OBB,
  b: OBB,
  maxHeightDiff = 250,
): boolean {
  if (!isUsableOBB(a) || !isUsableOBB(b)) return false
  try {
    if (Math.abs(a.center.y - b.center.y) > maxHeightDiff) return false
    return a.intersectsOBB(b)
  } catch {
    return false
  }
}

/** Solape real (no solo esquina de habitación ni muebles tocándose en la misma pared). */
export function significantCabinetOverlap(obbA: OBB, obbB: OBB): boolean {
  if (!obbOverlapAtSimilarHeight(obbA, obbB)) return false

  const centerDist = distanceXZ(obbA.center, obbB.center)
  const minCenterDist =
    (obbA.halfSize.x + obbB.halfSize.x) * 0.5 + (obbA.halfSize.z + obbB.halfSize.z) * 0.5

  // Si los centros están bastante separados, suele ser artefacto de esquina entre paredes
  if (centerDist > minCenterDist + 80) return false

  return centerDist < minCenterDist - 25
}

/**
 * Ancho vivo del mueble en escena (mm).
 * Free-dim: morph.default puede quedar en la ref de catálogo/clamp (p.ej. 350)
 * mientras item.width guarda el layout real (p.ej. 300). Priorizar siempre width.
 */
export function getEffectiveWidth(item: SceneObjects): number {
  if ('width' in item && typeof item.width === 'number' && item.width > 0) {
    return item.width
  }
  if ('morphs' in item && item.morphs?.length) {
    const widthMorph = item.morphs.find((m) => m.name === 'Ancho' || m.name === 'width')
    const active = widthMorph?._default ?? widthMorph?.default
    if (typeof active === 'number' && active > 0) return active
  }
  return 0
}

export function getWallLength(wall: Walls): number {
  return wall.width ?? Math.hypot(wall.end[0] - wall.start[0], wall.end[1] - wall.start[1])
}

export function formatMm(mm: number): string {
  if (mm >= 1000) return `${(mm / 1000).toFixed(2)} m`
  return `${Math.round(mm)} mm`
}

export function getHostCabinet(
  item: SceneObjects,
  items: Map<AnyID, SceneObjects>,
): SceneObjects | null {
  if (item.type === 'linkedItem') return item
  if (item.type === 'linkedSon' && 'firstParentID' in item && item.firstParentID) {
    const parent = items.get(item.firstParentID as AnyID)
    if (parent?.type === 'linkedItem') return parent
  }
  return null
}

export function getSnappedWallId(
  item: SceneObjects,
  items: Map<AnyID, SceneObjects>,
): string | undefined {
  if ('snappedWall' in item && item.snappedWall) return item.snappedWall
  const host = getHostCabinet(item, items)
  if (host && 'snappedWall' in host && host.snappedWall) return host.snappedWall
  return undefined
}

export function shareSameWall(
  a: SceneObjects,
  b: SceneObjects,
  items: Map<AnyID, SceneObjects>,
): boolean {
  const wallA = getSnappedWallId(a, items)
  const wallB = getSnappedWallId(b, items)
  return !!(wallA && wallB && wallA === wallB)
}

export function getItemSpanAlongWall(item: SceneObjects, wall: Walls): WallSpan | null {
  if (!('matrix' in item) || !item.matrix) return null

  const wallStart = toVector3(wall.start)
  const wallEnd = toVector3(wall.end)
  const wallDir = wallEnd.clone().sub(wallStart)
  const wallLen = wallDir.length()
  if (wallLen < 1) return null

  wallDir.normalize()
  const pos = new Vector3()
  item.matrix.decompose(pos, new Quaternion(), new Vector3())
  const along = pos.clone().sub(wallStart).dot(wallDir)
  const width = getEffectiveWidth(item)
  const wallWidth = getWallLength(wall)

  const start = Math.max(0, along)
  const end = Math.min(wallWidth, along + width)
  if (end <= start) return null
  return { start, end }
}

/** Hueco libre entre dos unidades a lo largo de la misma pared (mm). null si pared distinta. */
export function gapAlongSharedWall(
  a: SceneObjects,
  b: SceneObjects,
  items: Map<AnyID, SceneObjects>,
): number | null {
  if (!shareSameWall(a, b, items)) return null

  const wallId = getSnappedWallId(a, items)!
  const wall = items.get(wallId as AnyID)
  if (!wall || wall.type !== 'walls') return null

  const hostA = getHostCabinet(a, items) ?? a
  const hostB = getHostCabinet(b, items) ?? b
  const spanA = getItemSpanAlongWall(hostA, wall as Walls)
  const spanB = getItemSpanAlongWall(hostB, wall as Walls)

  if (spanA && spanB) {
    if (spanA.end <= spanB.start) return spanB.start - spanA.end
    if (spanB.end <= spanA.start) return spanA.start - spanB.end
    return 0
  }

  return distanceXZ(getItemCenter(a), getItemCenter(b))
}

/** Hueco (mm) a partir del cual dos unidades ordenadas dejan de ser el mismo tramo de laterales. */
export const LATERAL_GROUP_GAP_MM = 120

/**
 * Parte un grupo ya ordenado (izq→der) en subgrupos independientes cuando hay
 * un hueco real entre unidades contiguas. Misma regla que el Kitchen Inspector.
 *
 * El corte existe porque el grupo puede venir filtrado por familia (MAL/MBJ): dos entradas
 * seguidas de la lista no tienen por qué tocarse, porque en medio pueden haber quedado
 * fuera las de la otra familia. Cuando el registro de caras dice que SÍ se tocan no hay
 * hueco que medir, y hay que creerle: en un giro en L el mueble siguiente está rotado 90° y
 * desplazado por el fondo más la regleta del rinconero, así que la medida
 * centro-a-centro-menos-semianchos da siempre un hueco enorme y partía la fila en cada
 * esquina, dejando extremos falsos que se llevaban lateral.
 */
/** Vecinos según el registro de caras. El union SceneObjects no encaja de tipos con Snappable. */
type SnapAdjacentArg = Parameters<typeof areSnapAdjacent>[0]
const snapAdjacent = (a: SceneObjects, b: SceneObjects): boolean =>
  areSnapAdjacent(a as unknown as SnapAdjacentArg, b as unknown as SnapAdjacentArg)

export function splitOrderedGroupByRealGap<T extends { id: string | number }>(
  group: T[],
  items: Map<AnyID, SceneObjects>,
  gapThresholdMm: number = LATERAL_GROUP_GAP_MM,
): T[][] {
  if (group.length <= 1) return group.length ? [group] : []

  const result: T[][] = []
  let current: T[] = [group[0]]

  for (let i = 1; i < group.length; i++) {
    const prevEntry = current[current.length - 1]
    const nextEntry = group[i]
    const prev = items.get(prevEntry.id as AnyID)
    const next = items.get(nextEntry.id as AnyID)

    if (!prev || !next) {
      result.push(current)
      current = [nextEntry]
      continue
    }

    const gapOnWall = gapAlongSharedWall(prev, next, items)
    const prevCenter = getItemCenter(prev)
    const nextCenter = getItemCenter(next)
    const centerDistance = Math.hypot(prevCenter.x - nextCenter.x, prevCenter.z - nextCenter.z)
    const effectiveGap =
      centerDistance - (getEffectiveWidth(prev) + getEffectiveWidth(next)) / 2

    const clearlySeparated =
      !snapAdjacent(prev, next) &&
      ((gapOnWall !== null && gapOnWall > gapThresholdMm) ||
        (Number.isFinite(effectiveGap) && effectiveGap > gapThresholdMm))

    if (clearlySeparated) {
      result.push(current)
      current = [nextEntry]
    } else {
      current.push(nextEntry)
    }
  }

  result.push(current)
  return result
}

export function getHoleSpanAlongWall(hole: Hole): WallSpan {
  const start = Math.max(0, hole.distI ?? 0)
  const end = start + (hole.width ?? 0)
  return { start, end }
}

export function wallSpansOverlap(a: WallSpan, b: WallSpan): boolean {
  return a.start < b.end && b.start < a.end
}

/** La campana (KAC) está sobre la placa: más alta y solapada en planta. */
export const HOOD_MIN_ABOVE_PLACA_MM = 150

export type HoodAbovePlacaResult = {
  ok: boolean
  reason: string
  dy: number
  distXZ: number
  sameWall: boolean
  spansOverlap: boolean
}

export function isHoodAbovePlaca(
  placa: SceneObjects,
  hood: SceneObjects,
  items: Map<AnyID, SceneObjects>,
  sceneBoundingBoxes?: Map<string, BoundingBoxEntry>,
): HoodAbovePlacaResult {
  const placaHost = getHostCabinet(placa, items) ?? placa
  const hoodHost = getHostCabinet(hood, items) ?? hood
  const placaCenter = getItemCenter(placa, sceneBoundingBoxes)
  const hoodCenter = getItemCenter(hoodHost, sceneBoundingBoxes)
  const dy = hoodCenter.y - placaCenter.y
  const distXZ = distanceXZ(placaCenter, hoodCenter)
  const sameWall = shareSameWall(placaHost, hoodHost, items)

  if (dy < HOOD_MIN_ABOVE_PLACA_MM) {
    return {
      ok: false,
      reason: 'no-encima',
      dy,
      distXZ,
      sameWall,
      spansOverlap: false,
    }
  }

  if (sameWall) {
    const wallId = getSnappedWallId(placaHost, items)
    const wall = wallId ? items.get(wallId as AnyID) : undefined
    if (wall && wall.type === 'walls') {
      const spanPlaca = getItemSpanAlongWall(placaHost, wall)
      const spanHood = getItemSpanAlongWall(hoodHost, wall)
      const spansOverlap = !!(spanPlaca && spanHood && wallSpansOverlap(spanPlaca, spanHood))
      return {
        ok: spansOverlap,
        reason: spansOverlap ? 'misma-pared-solape' : 'misma-pared-sin-solape',
        dy,
        distXZ,
        sameWall,
        spansOverlap,
      }
    }
  }

  const coverRadius = Math.max(
    getEffectiveWidth(placaHost) * 0.55,
    getEffectiveWidth(hoodHost) * 0.55,
    250,
  )
  const ok = distXZ <= coverRadius
  return {
    ok,
    reason: ok ? 'cerca-xz' : 'lejos-xz',
    dy,
    distXZ,
    sameWall,
    spansOverlap: false,
  }
}

export function verticalOverlapWithHole(hole: Hole, item: SceneObjects): boolean {
  const holeBottom = hole.altitude ?? ('y' in hole ? hole.y : 0) ?? 0
  const holeTop = holeBottom + (hole.height ?? 0)
  const itemBottom = 'y' in item ? (item.y ?? 0) : 0
  const itemTop = itemBottom + ('height' in item ? (item.height ?? 0) : 900)
  return itemBottom < holeTop - 50 && holeBottom < itemTop - 50
}
